const compactTonsFormatter = new Intl.NumberFormat("fr-CA", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

export function formatTonsValue(value, fallback = "0.0") {
  if (!isFiniteNumber(value)) {
    return fallback;
  }

  return value.toFixed(1);
}

export function formatTons(value) {
  return formatTonsValue(value);
}

export function formatTonsWithUnit(value, { fallback = null, unit = "TM" } = {}) {
  if (!isFiniteNumber(value)) {
    return fallback;
  }

  return `${compactTonsFormatter.format(value)} ${unit}`;
}

export function getAssignmentTripCount(assignment) {
  if (!assignment || typeof assignment !== "object") {
    return 0;
  }

  if (isFiniteNumber(assignment.trip_count)) {
    return Math.max(assignment.trip_count, 0);
  }

  return Array.isArray(assignment.trips) ? assignment.trips.length : 0;
}

function normalizeDurationMinutes(value) {
  const durationMinutes = Number(value);
  return Number.isFinite(durationMinutes) && durationMinutes > 0 ? durationMinutes : 0;
}

function getTripDisplayedWorkloadMinutes(trip) {
  return Math.max(
    normalizeDurationMinutes(trip?.route?.estimated_duration_minutes),
    normalizeDurationMinutes(trip?.estimated_duration_minutes),
  );
}

function normalizeTruckCompatibilityReasonCode(compatibility) {
  return typeof compatibility?.reason_code === "string" ? compatibility.reason_code.trim().toLowerCase() : "";
}

function normalizeTruckCompatibilityTone(compatibility) {
  return typeof compatibility?.tone === "string" ? compatibility.tone.trim().toLowerCase() : "";
}

export function normalizeTruckCompatibilityTimeWindowMode(value) {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  return ["hard", "override_required", "soft", "informational"].includes(normalizedValue)
    ? normalizedValue
    : "";
}

export function getTruckCompatibilityTimeWindowMode(compatibility) {
  return normalizeTruckCompatibilityTimeWindowMode(compatibility?.time_window_mode);
}

export function isTruckCompatibilityActionable(compatibility) {
  if (!compatibility || compatibility.is_current_truck) {
    return false;
  }

  if (compatibility.is_assignable) {
    return true;
  }

  return getTruckCompatibilityTimeWindowMode(compatibility) === "override_required";
}

export function getAssignmentMaxWorkMinutes(assignment) {
  const effectiveMaxWorkMinutes = Number(assignment?.effective_max_work_minutes);
  if (Number.isFinite(effectiveMaxWorkMinutes) && effectiveMaxWorkMinutes > 0) {
    return Math.round(effectiveMaxWorkMinutes);
  }

  const maxWorkHours = Number(assignment?.max_work_hours);
  if (!Number.isFinite(maxWorkHours) || maxWorkHours <= 0) {
    return 0;
  }
  const configuredFlexMinutes = Number(assignment?.max_work_time_flex_minutes);
  const flexMinutes = Number.isFinite(configuredFlexMinutes) && configuredFlexMinutes >= 0
    ? configuredFlexMinutes
    : 0;
  return Math.round((maxWorkHours * 60) + flexMinutes);
}

export function getAssignmentDisplayedWorkloadMinutes(assignment) {
  const assignmentDurationMinutes = normalizeDurationMinutes(assignment?.total_estimated_duration_minutes);
  if (assignmentDurationMinutes > 0) {
    return assignmentDurationMinutes;
  }

  const tripDurationMinutes = Array.isArray(assignment?.trips)
    ? assignment.trips.reduce((total, trip) => total + getTripDisplayedWorkloadMinutes(trip), 0)
    : 0;

  return tripDurationMinutes;
}

export function isAssignmentOvertimeAuthorized(assignment, { authorizedTruckIds = null, truckId = "" } = {}) {
  if (assignment?.is_overtime_authorized === true) {
    return true;
  }

  if (!(authorizedTruckIds instanceof Set)) {
    return false;
  }

  const resolvedTruckId = typeof truckId === "string" && truckId.trim()
    ? truckId.trim()
    : (typeof assignment?.truck_id === "string" ? assignment.truck_id.trim() : "");

  return Boolean(resolvedTruckId) && authorizedTruckIds.has(resolvedTruckId);
}

export function getAssignmentOvertimeState(assignment, { warningThreshold = 0.75 } = {}) {
  const maxWorkMinutes = getAssignmentMaxWorkMinutes(assignment);
  const workloadMinutes = getAssignmentDisplayedWorkloadMinutes(assignment);
  const utilizationRatio = maxWorkMinutes > 0 ? workloadMinutes / maxWorkMinutes : 0;

  return {
    exceedsMaxWork: Boolean(maxWorkMinutes > 0 && workloadMinutes > maxWorkMinutes + 1e-9),
    maxWorkMinutes,
    shouldShow: Boolean(maxWorkMinutes > 0 && utilizationRatio >= warningThreshold),
    utilizationRatio,
    workloadMinutes,
  };
}

// Keep old truck status out of the Gantt until the reset result is installed.
// This lifecycle is independent of optimization animations and preview queues.
export function resolvePlanningResetTruckPresentation({
  dispatchDate,
  dispatchPeriod,
  launchScope = null,
  jobState = null,
  resultsData = null,
  appliedResetKey = "",
}) {
  const matchesScope = (scope) => Boolean(dispatchDate)
    && scope?.dispatchDate === dispatchDate
    && scope?.dispatchPeriod === dispatchPeriod;
  if (matchesScope(launchScope)) {
    return { suppressPreviousStatus: true, completedResetKey: "" };
  }
  if (jobState?.operationKind !== "planning_reset" || !matchesScope(jobState)) {
    return { suppressPreviousStatus: false, completedResetKey: "" };
  }

  const committed = jobState.resultCommitted === true
    || (jobState.status === "completed" && jobState.resultCommitted !== false);
  const active = ["running", "cancelling"].includes(jobState.status);
  const runId = jobState.optimizationDiagnostics?.run_id;
  const resetKey = jobState.jobId || runId || jobState.startedAt || "";
  const resultInstalled = (Boolean(resetKey) && appliedResetKey === resetKey)
    || (Boolean(runId)
      && resultsData?.dispatch_date === dispatchDate
      && resultsData?.optimization_diagnostics?.run_id === runId);
  return {
    suppressPreviousStatus: active || (committed && !resultInstalled),
    completedResetKey: committed && resultInstalled ? resetKey : "",
  };
}

// Orange et badge « Heures max dépassées » dès que le camion dépasse ses heures max,
// que le temps supplémentaire soit ouvert ou non (comme le panneau de droite).
export function shouldShowAssignmentOvertimeChrome(assignment) {
  return Boolean(getAssignmentOvertimeState(assignment).exceedsMaxWork);
}

export function getTargetedReactivateOvertimeState(assignment, targetCompatibility = null, { warningThreshold = 0.75 } = {}) {
  const overtimeState = getAssignmentOvertimeState(assignment, { warningThreshold });
  const compatibilityReasonCode = normalizeTruckCompatibilityReasonCode(targetCompatibility);
  const compatibilityTone = normalizeTruckCompatibilityTone(targetCompatibility);
  const timeWindowMode = getTruckCompatibilityTimeWindowMode(targetCompatibility);
  const compatibilitySignalsWarning = Boolean(
    targetCompatibility?.is_assignable
    && !timeWindowMode
    && (compatibilityReasonCode === "time_warning" || compatibilityTone === "warning")
  );

  return {
    ...overtimeState,
    compatibilityDetail: typeof targetCompatibility?.detail === "string" ? targetCompatibility.detail.trim() : "",
    compatibilityReasonCode,
    compatibilitySignalsWarning,
    compatibilityTone,
    timeWindowMode,
    shouldShow: overtimeState.shouldShow || compatibilitySignalsWarning,
  };
}

export function compareAssignmentsByDisplayedWorkload(leftAssignment, rightAssignment) {
  const leftLabel = String(leftAssignment?.truck_name || leftAssignment?.truck_id || "").trim();
  const rightLabel = String(rightAssignment?.truck_name || rightAssignment?.truck_id || "").trim();
  const sharedLabelOrder = leftLabel.localeCompare(rightLabel, "fr", { numeric: true, sensitivity: "base" });

  if (sharedLabelOrder !== 0) {
    return sharedLabelOrder;
  }

  const leftTruckId = String(leftAssignment?.truck_id || "").trim();
  const rightTruckId = String(rightAssignment?.truck_id || "").trim();
  return leftTruckId.localeCompare(rightTruckId, "fr", { numeric: true, sensitivity: "base" });
}

export function getDeliveryHighlightKey(deliveryId) {
  const safeDeliveryId = typeof deliveryId === "string" ? deliveryId.trim() : "";
  return safeDeliveryId ? `delivery:${safeDeliveryId}` : "";
}

export function getLoadingHighlightKey(loadingStepId) {
  const safeLoadingStepId = typeof loadingStepId === "string" ? loadingStepId.trim() : "";
  return safeLoadingStepId ? `loading:${safeLoadingStepId}` : "";
}

export function getLoadingOrderHighlightKey(orderId) {
  const safeOrderId = typeof orderId === "string" ? orderId.trim() : "";
  return safeOrderId ? `loading-order:${safeOrderId}` : "";
}

export function getInitialPreloadHighlightKey(loadingStepId) {
  const safeLoadingStepId = typeof loadingStepId === "string" ? loadingStepId.trim() : "";
  return safeLoadingStepId ? `initial-preload:${safeLoadingStepId}` : "";
}

export function getInitialPreloadOrderHighlightKey(orderId) {
  const safeOrderId = typeof orderId === "string" ? orderId.trim() : "";
  return safeOrderId ? `initial-preload-order:${safeOrderId}` : "";
}

export function getRouteSegmentHighlightKey(tripId, routeSegmentIndex) {
  const safeTripId = typeof tripId === "string" ? tripId.trim() : "";
  const safeRouteSegmentIndex = typeof routeSegmentIndex === "number" && Number.isInteger(routeSegmentIndex)
    ? String(routeSegmentIndex)
    : typeof routeSegmentIndex === "string"
      ? routeSegmentIndex.trim()
      : "";
  return safeTripId && safeRouteSegmentIndex ? `route-segment:${safeTripId}:${safeRouteSegmentIndex}` : "";
}

export function getSegmentBufferHighlightKey(segmentBufferKey) {
  const safeSegmentBufferKey = typeof segmentBufferKey === "string" ? segmentBufferKey.trim() : "";
  return safeSegmentBufferKey ? `segment-buffer:${safeSegmentBufferKey}` : "";
}

export function getClientSelectionId(target) {
  if (!target || typeof target !== "object") {
    return "";
  }

  const deliveryId = typeof target.delivery_id === "string" ? target.delivery_id.trim() : "";
  if (deliveryId) {
    return `delivery:${deliveryId}`;
  }

  const orderId = typeof target.order_id === "string" ? target.order_id.trim() : "";
  if (orderId) {
    return `order:${orderId}`;
  }

  return "";
}

function getClientSelectionEntry(state, clientId) {
  if (!clientId || !(state?.clientEntriesById instanceof Map)) {
    return null;
  }

  return state.clientEntriesById.get(clientId) || null;
}

export function setAssignmentOpenState(state, assignmentId, shouldOpen) {
  if (!assignmentId || !state) {
    return;
  }

  if (!(state.openTruckIds instanceof Set)) {
    state.openTruckIds = new Set();
  }

  if (shouldOpen) {
    state.openTruckIds.add(assignmentId);
    return;
  }

  state.openTruckIds.delete(assignmentId);
}

export function syncClientSelectionState(state) {
  if (!state) {
    return null;
  }

  const selectedClientId = typeof state.selectedClientId === "string" ? state.selectedClientId : "";
  const selectionEntry = selectedClientId ? getClientSelectionEntry(state, selectedClientId) : null;

  if (selectedClientId && !selectionEntry) {
    state.selectedClientId = null;
    return null;
  }

  if (selectionEntry?.assignmentId) {
    setAssignmentOpenState(state, selectionEntry.assignmentId, true);
  }

  return selectionEntry;
}

export function buildOperationalStepReassignmentKey(assignmentId, stepId) {
  if (!assignmentId || !stepId) {
    return "";
  }

  return `${assignmentId}::${stepId}`;
}

export function getTripClientItems(trip) {
  return Array.isArray(trip?.clients) ? trip.clients : [];
}

function filterOperationalStepCompatibilities(compatibilityEntries, { includeNonAssignable = false } = {}) {
  const entries = Array.isArray(compatibilityEntries) ? compatibilityEntries : [];

  return entries.filter((item) => {
    if (!item || item.is_current_truck) {
      return false;
    }

    if (includeNonAssignable) {
      return true;
    }

    return isTruckCompatibilityActionable(item);
  });
}

function getDeliveryPlanningItem(state, deliveryId) {
  if (!deliveryId) {
    return null;
  }

  return state?.deliveryItemsByOrderId?.get(deliveryId) || null;
}

function getOperationalStepRelatedClients(state, step) {
  if (!step || typeof step !== "object") {
    return [];
  }

  const trip = step.trip_id ? (state?.tripsById?.get(step.trip_id) || {}).trip || null : null;
  const clients = getTripClientItems(trip).filter(Boolean);
  if (!clients.length) {
    return [];
  }

  if (step.step_type === "delivery") {
    if (typeof step.delivery_id === "string" && step.delivery_id) {
      return clients.filter((client) => client.delivery_id === step.delivery_id);
    }

    return clients.filter((client) => client.order_id === step.order_id);
  }

  const matchingLoadingClients = clients.filter((client) => client.loading_step_id === step.step_id);
  if (matchingLoadingClients.length) {
    return matchingLoadingClients;
  }

  return clients.filter((client) => client.order_id === step.order_id);
}

function buildOperationalStepReassignmentContexts(state, step, { includeNonAssignable = false } = {}) {
  const relatedClients = getOperationalStepRelatedClients(state, step);
  const seenDeliveryIds = new Set();
  const contexts = [];

  relatedClients.forEach((client) => {
    const deliveryId = typeof client?.delivery_id === "string" ? client.delivery_id : "";
    if (!deliveryId || seenDeliveryIds.has(deliveryId)) {
      return;
    }

    seenDeliveryIds.add(deliveryId);

    const planningItem = getDeliveryPlanningItem(state, deliveryId);
    const baseItem = planningItem || client;
    const compatibilityEntries = Array.isArray(baseItem?.truck_compatibility)
      ? baseItem.truck_compatibility
      : Array.isArray(client?.truck_compatibility)
        ? client.truck_compatibility
        : [];

    contexts.push({
      deliveryId,
      clientName: baseItem?.client_name || client?.client_name || deliveryId,
      orderId: baseItem?.order_id || client?.order_id || step?.order_id || "",
      quantityTons: Number.isFinite(baseItem?.quantity_tons)
        ? baseItem.quantity_tons
        : Number.isFinite(client?.quantity_tons)
          ? client.quantity_tons
          : null,
      compatibilities: filterOperationalStepCompatibilities(compatibilityEntries, { includeNonAssignable }),
      isManual: baseItem?.assignment_origin === "manual",
      isPending: Boolean(state?.pendingDeliveryMutationById?.has?.(deliveryId)),
    });
  });

  if (!contexts.length && typeof step?.delivery_id === "string" && step.delivery_id) {
    const planningItem = getDeliveryPlanningItem(state, step.delivery_id);
    if (planningItem) {
      const compatibilityEntries = Array.isArray(planningItem.truck_compatibility) ? planningItem.truck_compatibility : [];
      contexts.push({
        deliveryId: step.delivery_id,
        clientName: planningItem.client_name || step.client_name || step.delivery_id,
        orderId: planningItem.order_id || step.order_id || "",
        quantityTons: Number.isFinite(planningItem.quantity_tons)
          ? planningItem.quantity_tons
          : Number.isFinite(step.quantity_tons)
            ? step.quantity_tons
            : null,
        compatibilities: filterOperationalStepCompatibilities(compatibilityEntries, { includeNonAssignable }),
        isManual: planningItem.assignment_origin === "manual",
        isPending: Boolean(state?.pendingDeliveryMutationById?.has?.(step.delivery_id)),
      });
    }
  }

  return contexts;
}

export function getOperationalStepReassignmentContexts(state, step) {
  return buildOperationalStepReassignmentContexts(state, step);
}

export function getOperationalStepReassignmentCandidatePoolContexts(state, step) {
  return buildOperationalStepReassignmentContexts(state, step, { includeNonAssignable: true });
}

export function getOperationalStepReassignmentAvailability(state, step) {
  const contexts = getOperationalStepReassignmentCandidatePoolContexts(state, step);
  const candidateCompatibilities = contexts.flatMap((context) => (
    Array.isArray(context?.compatibilities) ? context.compatibilities.filter(Boolean) : []
  ));
  const assignableCount = candidateCompatibilities.reduce(
    (total, compatibility) => total + (isTruckCompatibilityActionable(compatibility) ? 1 : 0),
    0,
  );
  const firstUnavailableReason = candidateCompatibilities.find((compatibility) => (
    !compatibility?.is_assignable && typeof compatibility?.detail === "string" && compatibility.detail.trim()
  ))?.detail?.trim() || "";

  return {
    assignableCount,
    hasAssignableTarget: assignableCount > 0,
    hasCandidatePool: candidateCompatibilities.length > 0,
    unavailableReason: assignableCount > 0
      ? ""
      : (firstUnavailableReason || "Aucun camion compatible n'est disponible pour cette étape dans la lecture courante."),
  };
}

function getActiveTripClientCount(trip) {
  const activeClients = Array.isArray(trip?.clients)
    ? trip.clients.filter((client) => client && !client.is_delivery_suspended)
    : [];

  if (activeClients.length > 0) {
    return activeClients.length;
  }

  if (Number.isFinite(trip?.client_count)) {
    return trip.client_count;
  }

  if (Number.isFinite(trip?.delivery_count)) {
    return trip.delivery_count;
  }

  return 0;
}

function getTripClientDisplayCount(trip) {
  const clientCount = getActiveTripClientCount(trip);
  if (clientCount > 0) {
    return clientCount;
  }

  if (Number.isFinite(trip?.client_count)) {
    return trip.client_count;
  }

  if (Number.isFinite(trip?.delivery_count)) {
    return trip.delivery_count;
  }

  return 0;
}

export function getTripRouteBadgeMeta(trip) {
  if (trip && (trip.primary_error_entry_id || Number(trip.invalid_client_count) > 0)) {
    return { label: "Route", tone: "danger" };
  }

  if (trip?.route?.status === "success") {
    return { label: "Route", tone: "success" };
  }

  if (trip?.route?.status === "error") {
    return { label: "Route", tone: "danger" };
  }

  return { label: "Plan", tone: "neutral" };
}

export function getTripSummaryLabel(trip) {
  const peakLoadLabel = typeof trip?.peak_load_tons === "number"
    ? `Pic ${formatTons(trip.peak_load_tons)} t`
    : `${formatTons(trip?.total_quantity_tons)} t`;

  return `${peakLoadLabel} - ${getTripClientDisplayCount(trip)} client(s)`;
}

export function buildFleetCards(state, interactions = null) {
  if (!state || !Array.isArray(state.assignmentList)) {
    return [];
  }

  return [...state.assignmentList].sort(compareAssignmentsByDisplayedWorkload).map((assignment) => {
    const assignmentId = assignment?.truck_id || "";
    const operationalSteps = Array.isArray(assignment?.operational_steps)
      ? assignment.operational_steps.filter(Boolean)
      : [];
    const deliveryItemsById = new Map(
      (Array.isArray(assignment?.delivery_items) ? assignment.delivery_items.filter(Boolean) : [])
        .map((item) => [item?.delivery_id || "", item]),
    );

    return {
      assignment,
      assignmentId,
      interactions: interactions && typeof interactions === "object" ? interactions.assignmentCard || null : null,
      isOpen: Boolean(state?.openTruckIds?.has?.(assignmentId)),
      isSelectedAssignment: state?.currentAssignment?.truck_id === assignmentId,
      openOperationalReassignmentKey: state?.openOperationalReassignmentKey || "",
      operationalSteps: operationalSteps.map((step) => ({
        reassignmentContexts: getOperationalStepReassignmentContexts(state, step),
        relatedDeliveryItem: typeof step?.delivery_id === "string"
          ? deliveryItemsById.get(step.delivery_id) || null
          : null,
        step,
      })),
      pendingDeliveryMutationIds: Array.from(state?.pendingDeliveryMutationById || []),
      pendingLoadingMutationIds: Array.from(state?.pendingLoadingMutationById || []),
      selectedClientId: state?.selectedClientId || "",
      transientActivatedStepKeys: Array.from(state?.transientActivatedStepKeys || []),
    };
  });
}
